import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Star, ThumbsUp, ThumbsDown, MessageSquare, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface FeedbackModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  decisionId: string | null;
  platform?: string;
  onFeedbackSubmitted?: () => void;
}

const FeedbackModal = ({
  open,
  onOpenChange,
  decisionId,
  platform = "unknown",
  onFeedbackSubmitted,
}: FeedbackModalProps) => {
  const [rating, setRating] = useState<number>(0);
  const [hoveredRating, setHoveredRating] = useState<number>(0);
  const [feedbackType, setFeedbackType] = useState<string>("");
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const feedbackOptions = [
    { value: "perfect", label: "Perfect as-is", description: "Ready to use immediately" },
    { value: "minor_edit", label: "Minor edits needed", description: "Small tweaks required" },
    { value: "major_edit", label: "Significant edits", description: "Needs substantial changes" },
    { value: "regenerate", label: "Need to regenerate", description: "Didn't meet expectations" },
  ];

  const handleSubmit = async () => {
    if (!decisionId) {
      toast.error("No decision to rate");
      return;
    }

    if (rating === 0) {
      toast.error("Please select a rating");
      return;
    }

    setIsSubmitting(true);
    try {
      const outcomeMap: Record<string, string> = {
        perfect: "success",
        minor_edit: "partial",
        major_edit: "partial",
        regenerate: "fail",
      };

      const { error } = await supabase.functions.invoke("track-outcome", {
        body: {
          decisionId,
          outcome: outcomeMap[feedbackType] || "pending",
          outcomeReason: `${feedbackType}: ${comment}`.trim(),
          outcomeScore: rating,
        },
      });

      if (error) throw error;

      toast.success("Thank you for your feedback!");
      onOpenChange(false);
      onFeedbackSubmitted?.();
      
      // Reset form
      setRating(0);
      setFeedbackType("");
      setComment("");
    } catch (error) {
      console.error("Error submitting feedback:", error);
      toast.error("Failed to submit feedback");
    } finally {
      setIsSubmitting(false);
    }
  };

  const displayRating = hoveredRating || rating;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-brand-500" />
            Rate This Output
          </DialogTitle>
          <DialogDescription>
            Your feedback helps us generate better content for you
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Star Rating */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Overall Quality</Label>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  className="p-1 transition-transform hover:scale-110"
                  onMouseEnter={() => setHoveredRating(star)}
                  onMouseLeave={() => setHoveredRating(0)}
                  onClick={() => setRating(star)}
                >
                  <Star
                    className={`h-8 w-8 transition-colors ${
                      star <= displayRating
                        ? "fill-brand-500 text-brand-500"
                        : "text-muted-foreground/30"
                    }`}
                  />
                </button>
              ))}
              <span className="ml-2 text-sm text-muted-foreground">
                {displayRating > 0 ? `${displayRating}/5` : "Select rating"}
              </span>
            </div>
          </div>

          {/* Quick Feedback Type */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">How usable was the output?</Label>
            <RadioGroup value={feedbackType} onValueChange={setFeedbackType} className="space-y-2">
              {feedbackOptions.map((option) => (
                <div
                  key={option.value}
                  className={`flex items-start space-x-3 p-3 rounded-lg border transition-colors cursor-pointer ${
                    feedbackType === option.value
                      ? "border-brand-300 bg-brand-50"
                      : "border-border hover:border-brand-200"
                  }`}
                  onClick={() => setFeedbackType(option.value)}
                >
                  <RadioGroupItem value={option.value} id={option.value} className="mt-0.5" />
                  <div className="flex-1">
                    <Label htmlFor={option.value} className="font-medium cursor-pointer">
                      {option.label}
                    </Label>
                    <p className="text-xs text-muted-foreground">{option.description}</p>
                  </div>
                </div>
              ))}
            </RadioGroup>
          </div>

          {/* Optional Comment */}
          <div className="space-y-2">
            <Label htmlFor="comment" className="text-sm font-medium">
              Additional Comments (optional)
            </Label>
            <Textarea
              id="comment"
              placeholder="What could make this better? Any specific improvements?"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              className="resize-none"
            />
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || rating === 0}
            className="bg-gradient-to-r from-brand-600 to-brand-500"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              "Submit Feedback"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FeedbackModal;
